import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Loading } from '@components/common';
import { Toggles, AlertTable } from './components';
import usePageTitle from '@src/hooks/usePageTitle';
import AlertForm from '../form';
import { FormActionContext } from '../form/context';
import { Confirmation } from '../shared';
import { useActions, InitEditAlert } from './hooks';
import useFetchAlerts from './hooks/useFetchAlerts';
import { DATE_SHORTCUTS } from '@utils/constants';
import { calcShortcutInterval } from '@utils';

const SummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 41px);
  position: relative;
`;

const filterAlertsByStatus = (allAlerts, status) => {
  switch (status) {
    case 'enabled':
      return allAlerts.filter((a) => a.state);
    case 'disabled':
      return allAlerts.filter((a) => !a.state);
    case 'triggered':
      return allAlerts.filter((a) => (a.events || []).length > 0);
    default:
      return allAlerts;
  }
};

const ManualAlertSummary = () => {
  const [status, setStatus] = useState<string>('all');
  const [visible, setVisible] = useState<boolean>(false);
  const [editAlert, setEditAlert] = useState({});
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState({});

  const initialInterval = useMemo(() => {
    const [from, to] = calcShortcutInterval(DATE_SHORTCUTS.sevenDays);
    return {
      from: from.format(),
      to: to.format(),
      shortcut: DATE_SHORTCUTS.sevenDays,
    };
  }, []);

  const [interval, setInterval] = useState(initialInterval);

  const { isFetching, isLoading, data } = useFetchAlerts(interval);
  const allAlerts = data.alerts;

  const onApplyInterval = useCallback((updatedInterval) => {
    setInterval(updatedInterval);
  }, []);

  usePageTitle({ text: 'Manual Alerts' });

  const { onCancel, onCancelConfirmation, onDelete, onEdit, onCreate } = useActions({
    setVisible,
    setEditAlert,
    setAlertToDelete,
    setConfirmationVisible,
  });

  InitEditAlert({
    allAlerts,
    setVisible,
    setEditAlert,
  });

  if (isLoading) {
    return (
      <SummaryWrapper>
        <Loading />
      </SummaryWrapper>
    );
  }

  const alerts = filterAlertsByStatus(allAlerts, status);

  return (
    <FormActionContext.Provider
      value={{
        formVisible: visible,
        actions: {
          onEdit,
          onDelete,
          onCreate,
          onCancel,
        },
      }}
    >
      <SummaryWrapper>
        <Toggles
          alerts={allAlerts}
          status={status}
          setStatus={setStatus}
          isFetching={isFetching}
          interval={interval}
          onApplyInterval={onApplyInterval}
        />
        <AlertTable
          key={JSON.stringify(alerts.map((a) => a.id))}
          alerts={alerts}
          eventCounts={data.eventCounts}
        />
        <AlertForm
          key={JSON.stringify(editAlert)}
          alert={editAlert}
          visible={visible}
          onCancel={onCancel}
        />
        <Confirmation
          visible={confirmationVisible}
          alert={alertToDelete}
          onCancel={onCancelConfirmation}
        />
      </SummaryWrapper>
    </FormActionContext.Provider>
  );
};

export default ManualAlertSummary;
